import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {InputNumber} from "primereact/inputnumber";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";

import {useMultipleParameterValues, useParameterValue} from "../../component/paramInput/paramHooks";
import ResetIcon from "../../component/paramInput/ResetIcon";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {navigateToRouteAndHash} from "../../hooks/navigationHooks";
import {abortModernizationDegreeQuery, queryModernizationDegreeAsync} from "../../redux/objectInformationThunk";
import {selectActiveValueForSingleParameterCurried} from "../../redux/objectParametersSelectors";
import {
    selectAllInspectionDerivedParams,
    selectAllInspectionParams,
    selectSegment,
} from "../../redux/objectParametersSlice";
import {selectReadOnly} from "../../redux/persistenceSlice";
import {useRevaStore} from "../../redux/revaHooks";
import {selectApiInputParamForCurrentSegmentCurried} from "../../redux/specSelectors";
import {selectSpec} from "../../redux/specSlice";

/**
 * Order of the modernization elements as used in the Besichtigungsbericht
 */
export const MODERNIZATION_ELEMENTS_ORDER = [
    "roofs_insulation",
    "exterior_doors_windows",
    "piping",
    "heating",
    "exterior_walls",
    "sanitary",
    "interior_walls_doors",
    "floor_plan",
];

const DERIVED_PARAMS = [
    "nhk_degree_of_modernization_weighted",
    "year_of_modernization_weighted",
    "nhk_degree_of_modernization",
    "year_of_modernization",
    "degree_modernization_category",
];

// Die AVM-Spec liefert für die Scores kein strukturiertes min/max, nur die Spanne im englischen
// Freitext ("... within [0,4]."); bricht das Format AVM-seitig, gibt es still undefined statt Fehler.
export function parseRangeFromDescription(description) {
    const start = description?.lastIndexOf("[");
    const end = description?.lastIndexOf("]");
    if (start == null || start === -1 || end === -1) return {min: undefined, max: undefined};
    const parts = description.slice(start + 1, end).split(",");
    if (parts.length !== 2) return {min: undefined, max: undefined};
    return {min: Number(parts[0]), max: Number(parts[1])};
}

// Gemeinsames Zeilen-Grundgerüst für Input- und Output-Ansicht der Modernisierungstabelle.
export function buildModernizationDegreeRows(inputParams, {heatingOnly = false} = {}) {
    return MODERNIZATION_ELEMENTS_ORDER.map((element) => `${element}_modernization_score`)
        .filter(
            (scoreParam) => inputParams[scoreParam] && (!heatingOnly || scoreParam === "heating_modernization_score"),
        )
        .map((scoreParam) => ({
            scoreParam,
            yearParam: scoreParam.replace("_modernization_score", "_modernization_year"),
            labelKey: scoreParam.replace("_modernization_score", ""),
        }));
}

function isOutOfRange(value, min, max) {
    return value != null && ((min != null && value < min) || (max != null && value > max));
}

function ColumnWarning({scoreParam, scoreMin, scoreMax, yearParam, yearMin, yearMax}) {
    const {t} = useTranslation();
    const scoreValue = useSelector(
        selectActiveValueForSingleParameterCurried(scoreParam, "INSPECTION_DERIVED"),
    )?.activeParameterValue;
    const yearValue = useSelector(
        selectActiveValueForSingleParameterCurried(yearParam, "INSPECTION_DERIVED"),
    )?.activeParameterValue;
    const scoreOutOfRange = isOutOfRange(scoreValue, scoreMin, scoreMax);
    const yearOutOfRange = isOutOfRange(yearValue, yearMin, yearMax);
    if (!scoreOutOfRange && !yearOutOfRange) return null;
    return (
        <div>
            {scoreOutOfRange && (
                <small className="p-error block">
                    {t("object.tab_inspection.input.value_out_of_range", {
                        min: scoreMin,
                        max: scoreMax,
                    })}
                </small>
            )}
            {yearOutOfRange && (
                <small className="p-error block">
                    {t("object.tab_inspection.input.value_out_of_range", {
                        min: yearMin,
                        max: yearMax,
                    })}
                </small>
            )}
        </div>
    );
}

function CellInput({param, maxFractionDigits, language, onAfterChange, disabled, min, max}) {
    const {parameterValues, onValueChange} = useParameterValue(param, "INSPECTION_DERIVED");
    const value = parameterValues?.activeParameterValue;
    const placeholder = max != null ? `max. ${max}` : undefined;

    return (
        <InputNumber
            value={value}
            minFractionDigits={0}
            maxFractionDigits={maxFractionDigits}
            inputClassName="text-right w-full"
            onValueChange={(e) => {
                if (e.originalEvent && e.value !== value) {
                    onValueChange(e.value);
                    onAfterChange();
                }
            }}
            locale={language}
            useGrouping={false}
            disabled={disabled}
            placeholder={placeholder}
            invalid={isOutOfRange(value, min, max)}
        />
    );
}

export function ObjectInputModernizationDegree({heatingOnly} = {}) {
    const {t, language} = useRevaTranslation();
    const dispatch = useDispatch();
    const store = useRevaStore();
    const navigate = useNavigate();
    const spec = useSelector(selectSpec);
    const segment = useSelector(selectSegment);
    const readOnly = useSelector(selectReadOnly);
    const inspectionDerived = useSelector(selectAllInspectionDerivedParams);
    const inputParams = spec?.apiParams?.MODERNIZATION_DEGREE?.[segment] || {};
    const yearOfModernizationSpec = useSelector(selectApiInputParamForCurrentSegmentCurried("year_of_modernization"));
    const {parameterValues: yearOfConstructionValues} = useParameterValue("year_of_construction", "AVM");
    const yearMin = yearOfConstructionValues?.activeParameterValue;
    const yearMax = yearOfModernizationSpec?.maxValue;
    const {parameterValues: nhkParams, onValuesChange} = useMultipleParameterValues(
        ["nhk_degree_of_modernization_weighted"],
        "INSPECTION_DERIVED",
    );

    const rows = buildModernizationDegreeRows(inputParams, {heatingOnly}).map((row) => {
        const {min: scoreMin, max: scoreMax} = parseRangeFromDescription(inputParams[row.scoreParam]?.descriptionEn);
        return {
            ...row,
            scoreMin,
            scoreMax,
            label: t(`object.tab_inspection.output.degree_of_modernization_table.${row.labelKey}`),
        };
    });

    // Geprüft wird jede Zeile, nicht nur die bearbeitete, denn die Abfrage schickt alle mit: Scores begrenzt die AVM
    // still, statt sie abzulehnen, Jahre übernimmt sie ungeprüft (die Jahresgrenzen sind REVA-Regeln).
    // Quelle wie beim Senden: eigene Werte, sonst Besichtigung.
    const anyValueOutOfRange = () => {
        const state = store.getState();
        const derived = selectAllInspectionDerivedParams(state);
        const inspection = selectAllInspectionParams(state);
        const valueFor = (param) => derived?.[param] ?? inspection?.[param];
        return rows.some(
            (row) =>
                isOutOfRange(valueFor(row.scoreParam), row.scoreMin, row.scoreMax) ||
                isOutOfRange(valueFor(row.yearParam), yearMin, yearMax),
        );
    };

    const fetchModernizationDegree = async () => {
        if (readOnly) return;
        if (anyValueOutOfRange()) {
            abortModernizationDegreeQuery();
            const derived = selectAllInspectionDerivedParams(store.getState());
            const stored = DERIVED_PARAMS.filter((param) => derived?.[param] != null);
            if (stored.length > 0) onValuesChange(Object.fromEntries(stored.map((param) => [param, null])));
            return;
        }

        // #19376: fail hard — jede Antwort setzt alle fünf Felder neu, ein in der Antwort fehlendes Feld
        // oder ein fehlgeschlagener Request (dann liefert der Thunk ein leeres Ergebnis) nullt es, damit nie mit
        // veralteten Ableitungen weitergerechnet wird.
        const result = await dispatch(queryModernizationDegreeAsync(inputParams));
        if (!result) return;
        onValuesChange({
            nhk_degree_of_modernization: result.nhk_degree_of_modernization ?? null,
            nhk_degree_of_modernization_weighted: result.nhk_degree_of_modernization ?? null,
            year_of_modernization_weighted: result.year_of_modernization_weighted ?? null,
            year_of_modernization: result.year_of_modernization_weighted ?? null,
            degree_modernization_category: result.degree_modernization_category ?? null,
        });
    };

    const canReset =
        !readOnly &&
        (nhkParams.nhk_degree_of_modernization_weighted?.activeParameterValue != null ||
            rows.some(
                (row) => inspectionDerived?.[row.scoreParam] != null || inspectionDerived?.[row.yearParam] != null,
            ));

    const handleReset = () => {
        abortModernizationDegreeQuery();
        // Auch die AVM-Inputs löschen, die fetchModernizationDegree aus den Zeilen ableitet — sonst rechnet die
        // AVM mit Werten aus bereits gelöschten Eingaben weiter (#19372)
        const updates = Object.fromEntries(DERIVED_PARAMS.map((param) => [param, null]));
        rows.forEach((row) => {
            updates[row.scoreParam] = null;
            updates[row.yearParam] = null;
        });
        onValuesChange(updates);
    };

    const descriptionHeader = (
        <span className="flex items-center gap-1">
            <span className="p-column-title">{t("object.tab_inspection.input.col_description")}</span>
            {canReset && !heatingOnly && <ResetIcon parameter="modernization_table" onValueChange={handleReset} />}
        </span>
    );

    const onHeatingOnlyClick = heatingOnly
        ? () => navigateToRouteAndHash(navigate, "/object/inspection", "input_inspection_degree_of_modernization")
        : undefined;

    return (
        <DataTable
            id="input_inspection_degree_of_modernization"
            value={rows}
            size="small"
            className="p-inputtext-sm text-sm inputtext-compact"
            footer={<div />}
        >
            <Column field="label" header={heatingOnly ? undefined : descriptionHeader} />
            <Column
                bodyClassName="text-right"
                body={(row) => (
                    <ColumnWarning
                        scoreParam={row.scoreParam}
                        scoreMin={row.scoreMin}
                        scoreMax={row.scoreMax}
                        yearParam={row.yearParam}
                        yearMin={yearMin}
                        yearMax={yearMax}
                    />
                )}
            />
            <Column
                header={t("object.tab_inspection.output.degree_of_modernization_table.col_score")}
                headerClassName="p-align-right w-24"
                body={(row) => (
                    <div onClick={onHeatingOnlyClick} className={heatingOnly ? "cursor-pointer" : undefined}>
                        <CellInput
                            param={row.scoreParam}
                            maxFractionDigits={2}
                            language={language}
                            onAfterChange={fetchModernizationDegree}
                            disabled={readOnly || heatingOnly}
                            min={row.scoreMin}
                            max={heatingOnly ? undefined : row.scoreMax}
                        />
                    </div>
                )}
            />
            <Column
                header={t("object.tab_inspection.output.degree_of_modernization_table.col_year")}
                headerClassName="p-align-right w-28"
                body={(row) => (
                    <div onClick={onHeatingOnlyClick} className={heatingOnly ? "cursor-pointer" : undefined}>
                        <CellInput
                            param={row.yearParam}
                            maxFractionDigits={0}
                            language={language}
                            onAfterChange={fetchModernizationDegree}
                            disabled={readOnly || heatingOnly}
                            min={yearMin}
                            max={heatingOnly ? undefined : yearMax}
                        />
                    </div>
                )}
            />
        </DataTable>
    );
}
